import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useEffect, useRef, useState, KeyboardEvent } from 'react';
import { GridInitialState } from '@mui/x-data-grid-pro';
import { GridLogicOperator, GridFilterItem } from '@mui/x-data-grid-pro';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../utils/styleHelpers';
import {
  getCustomizationSubject,
  gridInitialState,
  updateUserCustomizations,
  GridPreferences,
  PrefKey,
  UNCONFIRMED_WRITE_MESSAGE,
  UserCustomizations
} from '../../../store/UserCustomizations';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { DefaultAssetGridColumnDefs } from '../../Pages/Asset/AssetGridColumnDefs';
import { DefaultLinkGridColumnDefs } from '../../Pages/Link/LinkGridColumnDefs';
import { DefaultRPMGridColumnDefs } from '../../Pages/RPM/RPMGridColumnDefs';
import { DefaultCustomerGridColumnDefs } from '../../Pages/Customer/CustomerGridColumnDefs';
import { DefaultProvisionalAssetGridColumnDefs } from '../../Pages/ProvisionalAsset/ProvisionalAssetGridColumnDefs';
import { DefaultTicketGridColumnDefs } from '../../Pages/Ticket/TicketGridColumnDefs';
import { DefaultTFUGridColumnDefs } from '../../Pages/TFU/TFUGridColumnDefs';
import { TextBlock } from '../../Atoms/Text';
import { FilterableField } from '../../../store/GeneralStore';
import { mergeAndDeduplicateFilters } from '../UpstackDataGrid/helpers';
import { DefaultPaymentGridColumnDefs } from '../../Pages/Payment/PaymentGridColumnDefs';
import { DefaultCorrectionGridColumnDefs } from '../../Pages/Correction/CorrectionGridColumnDefs';
import { DefaultAdjustmentGridColumnDefs } from '../../Pages/Adjustment/AdjustmentGridColumnDefs';
import { DefaultAttributionGridColumnDefs } from '../../Pages/Attribution/DirectSeller/GridColumnDefs';
import { DefaultHistoryGridColumnDefs } from '../../Pages/History/HistoryGridColumnDefs';

export interface NewViewModalParams {
  open: boolean;
  setOpen: (state: boolean) => void;
  sourceTab: string;
  type:
    | 'asset'
    | 'RPM'
    | 'link'
    | 'customer'
    | 'provisionalAsset'
    | 'ticket'
    | 'tfu'
    | 'payment'
    | 'correction'
    | 'adjustment'
    | 'attribution'
    | 'history';
  onViewCreated?: (viewName: string) => void;
  sourceTabFilter?: FilterableField[];
}

export const NewViewModal = ({
  open,
  setOpen,
  sourceTab,
  type,
  onViewCreated,
  sourceTabFilter
}: NewViewModalParams) => {
  const { setSnack } = useSnackbar();
  const [userPreferences, setUserPreferences] = useState<UserCustomizations>({} as UserCustomizations);
  const [newName, setNewName] = useState('');
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const prefSub = getCustomizationSubject().subscribe((prefs) => {
      if (prefs) setUserPreferences(prefs);
    });

    return () => {
      if (prefSub) prefSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        nameInputRef.current?.focus();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [open]);

  const getGridDefsByType = (type: NewViewModalParams['type']): GridInitialState => {
    switch (type) {
      case 'asset':
        return gridInitialState(DefaultAssetGridColumnDefs()) as GridInitialState;
      case 'link':
        return gridInitialState(DefaultLinkGridColumnDefs()) as GridInitialState;
      case 'RPM':
        return gridInitialState(DefaultRPMGridColumnDefs) as GridInitialState;
      case 'customer':
        return gridInitialState(DefaultCustomerGridColumnDefs()) as GridInitialState;
      case 'provisionalAsset':
        return gridInitialState(DefaultProvisionalAssetGridColumnDefs()) as GridInitialState;
      case 'ticket':
        return gridInitialState(DefaultTicketGridColumnDefs) as GridInitialState;
      case 'tfu':
        return gridInitialState(DefaultTFUGridColumnDefs()) as GridInitialState;
      case 'payment':
        return gridInitialState(DefaultPaymentGridColumnDefs()) as GridInitialState;
      case 'correction':
        return gridInitialState(DefaultCorrectionGridColumnDefs()) as GridInitialState;
      case 'adjustment':
        return gridInitialState(DefaultAdjustmentGridColumnDefs()) as GridInitialState;
      case 'attribution':
        return gridInitialState(DefaultAttributionGridColumnDefs()) as GridInitialState;
      case 'history':
        return gridInitialState(DefaultHistoryGridColumnDefs()) as GridInitialState;
      default:
        return gridInitialState([]) as GridInitialState;
    }
  };

  const handleSubmitForm = async () => {
    const currentSettings: UserCustomizations = JSON.parse(JSON.stringify(userPreferences));
    if (currentSettings && currentSettings.content) {
      let newView: GridPreferences = {
        customView: true,
        muiConfig: getGridDefsByType(type),
        disabledFields: []
      };
      const currentTabTableState = JSON.parse(
        JSON.stringify(
          currentSettings?.content?.[`${type}Grid_${sourceTab.replace(/\s/g, '')}` as PrefKey] ||
            currentSettings?.content?.[`${type}Grid_${sourceTab}` as PrefKey]
        )
      );
      newView = {
        ...newView,
        ...currentTabTableState
      };
      const userAppliedGridFilters: GridFilterItem[] = newView.muiConfig?.filter?.filterModel?.items || [];
      const userAppliedFilters: FilterableField[] = userAppliedGridFilters
        .filter(
          (item): item is Omit<GridFilterItem, 'value'> & { value: string | number | string[] | number[] } =>
            item.value !== undefined && item.value !== null && item.value !== ''
        )
        .map(({ field, operator, value }) => ({
          field,
          operator,
          value
        }));
      const predefinedFilters = sourceTabFilter || [];
      const combinedFilters = mergeAndDeduplicateFilters(userAppliedFilters, predefinedFilters);
      if (!newView.muiConfig.filter) {
        newView.muiConfig.filter = { filterModel: undefined };
      }
      if (!newView.muiConfig.filter.filterModel) {
        newView.muiConfig.filter.filterModel = { items: [], logicOperator: 'and' as GridLogicOperator };
      }
      newView.muiConfig.filter.filterModel.items = combinedFilters;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (currentSettings.content as Record<PrefKey, any>)[`${type}Grid_${newName}` as PrefKey] = newView;
      const { data, error } = await updateUserCustomizations(currentSettings, `${type}Grid_${newName}`, true);
      if (data) {
        setSnack({ message: 'Your view was created successfully', type: 'success', open: true });
        if (onViewCreated) onViewCreated(newName);
      } else {
        setSnack({
          message: `There was an error creating your view: \n${error?.message ?? UNCONFIRMED_WRITE_MESSAGE}`,
          type: 'error',
          open: true
        });
      }

      handleClose();
    }
  };

  const handleClose = () => {
    setNewName('');
    setOpen(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && newName.trim() !== '') {
      e.preventDefault();
      handleSubmitForm();
    }
  };

  return (
    <Dialog
      data-testid="new-view-modal"
      maxWidth="xs"
      open={open}
      onClose={handleClose}
      fullWidth>
      <DialogTitle>
        <TextBlock
          size="lg"
          color="grey9">
          Save as View
        </TextBlock>
      </DialogTitle>
      <DialogContent sx={{ minHeight: 70, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="space-y-5 justify-center w-full">
          <TextField
            inputRef={nameInputRef}
            required
            data-testid="new-view-name-input"
            sx={{ width: '100%' }}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="View Name *"
            size="small"
          />
        </div>
      </DialogContent>
      <hr className="mb-1" />
      <DialogActions sx={{ marginRight: 2, pb: 2 }}>
        <button
          className={DEFAULT_CANCEL_BUTTON_STYLE}
          onClick={handleClose}>
          Cancel
        </button>
        <button
          data-testid="submit-new-view"
          disabled={!newName}
          onClick={handleSubmitForm}
          className={DEFAULT_ADVANCE_BUTTON_STYLE}
          type="submit">
          Save
        </button>
      </DialogActions>
    </Dialog>
  );
};
